import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import { Button, FormControlLabel, Grid, Switch, Typography } from '@mui/material';
import { GridRowModesModel, useGridApiRef } from '@mui/x-data-grid';
import { QueryFunctionContext, useQuery } from '@tanstack/react-query';
import { includes, isEmpty, map, orderBy, remove, size } from 'es-toolkit/compat';
import React, { useMemo } from 'react';
import { BooleanParam, useQueryParam, withDefault } from 'use-query-params';

import { ANNOTATIONS_BASE_QUERY_KEY, getAllAnnotationAssignments } from 'api/annotationAssignments';
import { SettingsDataGrid } from 'components/atoms/BaseDataGrid/SettingsDataGrid';
import { useTableEditingContext } from 'components/atoms/EditableDataGrid/TableEditingContext';
import { useRowSelectionContext } from 'components/atoms/RowSelectionContext';
import { getCheckboxColumnWithClearSelection } from 'components/atoms/RowSelectionContext/checkboxColumn';
import Loader, { LoadingStage } from 'components/Loader';
import { useAnnotationAssignments } from 'utils/useAnnotationAssignments';
import { BulkTagActionDialog } from './BulkTagActionDialog';
import { AnnotationAssignmentFieldContext } from './fields';
import { useAnnotationAssignmentsColumns } from './useAnnotationAssignmentsColumns';

const getRowId = (row: any) => row.annotationAssignmentId;

interface Props {
  studyId?: string;
}

const AnnotationAssignmentTaggingDialog: React.FC<Props> = ({ studyId }) => {
  const [fetchAllAssignments, setFetchAllAssignments] = useQueryParam(
    'fetchAllAssignments',
    withDefault(BooleanParam, true)
  );
  const [showLegacyAssignments, setShowLegacyAssignments] = useQueryParam('showLegacyAssignments', BooleanParam);
  const [showOnlySlidesOfStudy, setShowOnlySlidesOfStudy] = useQueryParam(
    'showOnlySlidesOfStudy',
    withDefault(BooleanParam, true)
  );

  React.useEffect(() => {
    if (!studyId && !fetchAllAssignments) {
      setFetchAllAssignments(true);
    }
  }, [fetchAllAssignments, setFetchAllAssignments, studyId]);

  const canScopeToStudy = Boolean(studyId);
  const {
    isFetched: isSpecificAssignmentsFetched,
    queryKey: specificAnnotationAssignmentsQueryKey,
    data: specificAnnotationAssignments,
    isLoading: isSpecificAssignmentIdsLoading,
  } = useAnnotationAssignments(studyId, showLegacyAssignments, showOnlySlidesOfStudy, !fetchAllAssignments);

  const allAnnotationAssignmentsQueryKey = [ANNOTATIONS_BASE_QUERY_KEY, 'all'];
  const {
    data: allAnnotationAssignments,
    isLoading: isAllAssignmentLoading,
    isFetched: isAllAssignmentFetched,
  } = useQuery({
    queryKey: allAnnotationAssignmentsQueryKey,
    queryFn: ({ signal }: QueryFunctionContext) => getAllAnnotationAssignments({ isGlobal: false, signal }),
    enabled: Boolean(fetchAllAssignments),
  });

  const queryKey = fetchAllAssignments ? allAnnotationAssignmentsQueryKey : specificAnnotationAssignmentsQueryKey;
  const isLoading = fetchAllAssignments ? isAllAssignmentLoading : isSpecificAssignmentIdsLoading;
  const isFetched = fetchAllAssignments ? isAllAssignmentFetched : isSpecificAssignmentsFetched;
  const annotationAssignments = fetchAllAssignments
    ? allAnnotationAssignments
    : specificAnnotationAssignments?.annotationAssignments;

  const queriedAnnotationAssignmentIds = useMemo(
    () => map(annotationAssignments, 'annotationAssignmentId'),
    [annotationAssignments]
  );

  const sortedAnnotationAssignments = useMemo(
    () => orderBy(annotationAssignments, 'annotationAssignmentId', 'desc'),
    [annotationAssignments]
  );

  const totalRows = size(annotationAssignments);

  const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>({});

  const apiRef = useGridApiRef();

  const { fieldsContext } = useTableEditingContext<any, AnnotationAssignmentFieldContext>();
  const [bulkTagActionDialogOpen, setBulkTagActionDialogOpen] = React.useState(false);

  const { someSelected, allSelected } = useRowSelectionContext();
  const hasSomeSelection = totalRows > 0 && (someSelected(totalRows) || allSelected(totalRows));

  const columns = useAnnotationAssignmentsColumns({
    isLoading,
    bulkEditMode: false,
    queryKey,
    noRows: isEmpty(sortedAnnotationAssignments),
    apiRef,
    currentRows: sortedAnnotationAssignments,
    rowModesModel,
    fieldsContext,
    setRowModesModel,
  });

  const columnsWithSelectionCheckbox = useMemo(
    // We use clear instead of select all since syncing the selection state with the quick text filter is complex, and the use case doesn't require select all
    () => [getCheckboxColumnWithClearSelection(totalRows), ...columns],
    [totalRows, columns]
  );

  const { selectedRows: selectedRowsState, omittedRows, selectionMode } = useRowSelectionContext();

  // Only pass ids when there is an actual selection
  const annotationAssignmentIdsToInclude =
    selectionMode === 'select'
      ? !isEmpty(selectedRowsState)
        ? (selectedRowsState as number[])
        : undefined
      : !fetchAllAssignments
      ? // When not fetching all assignments, we can't use omitted rows so we pass them as included rows based on the query
        remove(queriedAnnotationAssignmentIds, (id) => includes(omittedRows, id))
      : undefined;
  // Only pass ids to exclude when there is an actual omission and we are fetching all assignments
  const annotationAssignmentIdsToExclude =
    selectionMode === 'omit' && fetchAllAssignments ? (omittedRows as number[]) : undefined;

  const rowSelectionModel = useMemo(
    () =>
      annotationAssignmentIdsToInclude ||
      remove(queriedAnnotationAssignmentIds, (id) => includes(annotationAssignmentIdsToExclude, id)),
    [annotationAssignmentIdsToInclude, annotationAssignmentIdsToExclude, queriedAnnotationAssignmentIds]
  );
  const selectedAssignmentCount = size(rowSelectionModel);
  const selectedAssignments = useMemo(
    () =>
      sortedAnnotationAssignments.filter((assignment) =>
        includes(rowSelectionModel, assignment.annotationAssignmentId)
      ),
    [rowSelectionModel, sortedAnnotationAssignments]
  );
  const selectedAnnotationAssignmentIds = useMemo(
    () => map(selectedAssignments, 'annotationAssignmentId'),
    [selectedAssignments]
  );

  return (
    <Grid container direction="column">
      <Grid item container direction="row" alignItems="center" spacing={2} py={1} xs={6} justifyContent="flex-end">
        <Grid item mr="auto">
          <FormControlLabel
            control={
              <Switch
                checked={fetchAllAssignments}
                disabled={!canScopeToStudy}
                onChange={(e) => setFetchAllAssignments(e.target.checked)}
              />
            }
            label="Fetch annotation assignments from all labs"
          />
        </Grid>
        <Grid item>
          <Button
            variant="outlined"
            startIcon={<LocalOfferOutlinedIcon />}
            disabled={!hasSomeSelection}
            onClick={() => setBulkTagActionDialogOpen(true)}
          >
            Bulk tag actions
          </Button>
        </Grid>
        <Grid item>
          <FormControlLabel
            control={
              <Switch
                disabled={fetchAllAssignments}
                checked={showLegacyAssignments}
                onChange={(e) => setShowLegacyAssignments(e.target.checked)}
              />
            }
            label="Show legacy assignments of this study's slides"
          />
        </Grid>
        <Grid item>
          <FormControlLabel
            control={
              <Switch
                disabled={fetchAllAssignments}
                checked={showOnlySlidesOfStudy}
                onChange={(e) => setShowOnlySlidesOfStudy(e.target.checked)}
              />
            }
            label="Show only slides of this study"
          />
        </Grid>
      </Grid>
      <Grid item container>
        {isFetched && isEmpty(sortedAnnotationAssignments) ? (
          <Grid container direction="column" alignItems="center" justifyContent="center" spacing={10} px={10} pt={3}>
            <Grid item>
              <Typography variant="h4">No annotation assignments found</Typography>
            </Grid>
          </Grid>
        ) : !isLoading ? (
          <SettingsDataGrid
            autoHeight={false}
            height="80vh"
            initialPageSize={25}
            editMode="row"
            bulkEditMode={false}
            checkboxSelection
            apiRef={apiRef}
            rows={sortedAnnotationAssignments}
            columns={columnsWithSelectionCheckbox}
            rowModesModel={rowModesModel}
            onRowModesModelChange={setRowModesModel}
            getRowId={getRowId}
            rowSelectionModel={rowSelectionModel}
            sx={{
              '& .MuiDataGrid-columnHeaderTitleContainer': {
                margin: 'auto',
              },
            }}
          />
        ) : (
          <Loader stage={LoadingStage.LoadingTaskData} />
        )}
      </Grid>
      <BulkTagActionDialog
        open={bulkTagActionDialogOpen}
        onClose={() => setBulkTagActionDialogOpen(false)}
        annotationAssignmentIdsToInclude={selectedAnnotationAssignmentIds}
        selectedAssignments={selectedAssignments}
        selectedAssignmentCount={selectedAssignmentCount}
        tagOptions={fieldsContext.tagOptions}
      />
    </Grid>
  );
};

export default AnnotationAssignmentTaggingDialog;
